import { css } from "@emotion/react";
import { type KeyboardEvent, useLayoutEffect, useRef } from "react";

import { sanitizeHtml } from "../../sanitizeHtml";
import {
    applyEditableContent,
    applyFocus,
    type CaretContentSplit,
    isCaretAtStart,
    splitContentAtCaret,
} from "../utilities/caret";
import { type PastedSegment, splitClipboardIntoBlocks } from "../utilities/paste";

type EditableProps = {
    value: string;
    onChange: (value: string) => void;
    mode: "text" | "html";
    placeholder?: string;
    autoFocus?: false | "start" | "end" | number;
    allowLineBreak?: boolean;
    onEnter?: (split: CaretContentSplit) => void;
    onBackspace?: () => void;
    onPasteBlocks?: (segments: PastedSegment[], caret: CaretContentSplit) => void;
    onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
};

export default function Editable(props: EditableProps) {
    const {
        value,
        onChange,
        mode,
        placeholder,
        autoFocus,
        allowLineBreak = true,
        onEnter,
        onBackspace,
        onPasteBlocks,
        onKeyDown,
    } = props;
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!ref.current) return;
        const current =
            mode === "html" ? sanitizeHtml(ref.current.innerHTML) : (ref.current.textContent ?? "");
        if (current === value) return;
        applyEditableContent(ref.current, value, mode);
    }, [mode, value]);

    useLayoutEffect(() => {
        if (autoFocus === false || autoFocus === undefined || !ref.current) return;
        applyFocus(ref.current, autoFocus);
    }, [autoFocus]);

    const emitChange = () => {
        if (!ref.current) return;
        if (mode === "html") onChange(sanitizeHtml(ref.current.innerHTML));
        else onChange(ref.current.textContent ?? "");
    };

    const editorCss = css({
        outline: "none",
        minHeight: "1em",
        width: "100%",
        "&:empty:before": placeholder
            ? {
                  content: `"${placeholder}"`,
                  opacity: 0.4,
              }
            : undefined,
    });

    return (
        <div
            ref={ref}
            css={editorCss}
            contentEditable
            suppressContentEditableWarning
            data-placeholder={placeholder}
            onInput={emitChange}
            onKeyDown={(event) => {
                if (event.nativeEvent.isComposing) return;
                onKeyDown?.(event);
                if (event.defaultPrevented) return;
                if (event.key === "Enter") {
                    const shouldSplit = Boolean(onEnter) && (!event.shiftKey || !allowLineBreak);
                    if (shouldSplit && onEnter) {
                        event.preventDefault();
                        if (!ref.current) return;
                        const split = splitContentAtCaret(ref.current, mode);
                        if (split.position === "middle") {
                            applyEditableContent(ref.current, split.before, mode);
                        }
                        ref.current.blur();
                        onEnter(split);
                        return;
                    }
                    if (mode === "text" || !allowLineBreak) {
                        event.preventDefault();
                        return;
                    }
                }
                if (
                    event.key === "Backspace" &&
                    onBackspace &&
                    ref.current &&
                    isCaretAtStart(ref.current)
                ) {
                    event.preventDefault();
                    onBackspace();
                    return;
                }
                if (mode === "html" && (event.metaKey || event.ctrlKey) && event.key === "b") {
                    event.preventDefault();
                    document.execCommand("bold");
                    emitChange();
                }
            }}
            onPaste={(event) => {
                event.preventDefault();
                if (mode === "text") {
                    const text = event.clipboardData.getData("text/plain");
                    document.execCommand("insertText", false, text.replace(/\n/g, " "));
                    emitChange();
                    return;
                }
                const html = event.clipboardData.getData("text/html");
                const text = event.clipboardData.getData("text/plain");
                if (onPasteBlocks && ref.current) {
                    const segments = splitClipboardIntoBlocks(html, text);
                    if (segments.length === 0) return;
                    const caret = splitContentAtCaret(ref.current, mode);
                    const first = segments[0];
                    if (first?.type === "body") {
                        applyEditableContent(ref.current, caret.before + first.html, mode);
                    }
                    ref.current.blur();
                    onPasteBlocks(segments, caret);
                    return;
                }
                const inserted = html ? sanitizeHtml(html) : escapeText(text);
                document.execCommand("insertHTML", false, inserted);
                emitChange();
            }}
        />
    );
}

function escapeText(text: string): string {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
}
